import { Base64 } from 'js-base64';
import ukraineProductMapping from '../ukraine-product-list-mapping.json';

const UKRAINE_SEED_SELECTOR_BASE_URL = 'https://store.syngenta.ua/seed-selector/';

// ============================================
// NORMALIZATION & FUZZY MATCHING UTILITIES
// ============================================

/**
 * Aliases for Ukrainian words in hybrid_name that have no resemblance to their
 * English equivalents in sfdcProductCode. Only true translations need aliases —
 * close transliterations (e.g. cruiser/cruizer) are handled automatically by
 * fuzzy token matching (Levenshtein distance ≤ 1).
 */
const ALIAS_MAP: Record<string, string> = {
  neprotruyene: 'non treated',
  'bez protruiky': 'non treated',
  vaibrans: 'vibrance',
  fors: 'force',
};

/** Normalize a string: lowercase, unify separators to space, collapse whitespace. */
const normalize = (str: string): string =>
  str
    .toLowerCase()
    .replaceAll(/[_\-+\s]+/g, ' ')
    .trim()
    .replaceAll(/\s+/g, ' ');

/** Apply alias substitutions (whole-word only) to a normalized string. */
const applyAliases = (normalized: string): string => {
  let result = normalized;
  for (const [from, to] of Object.entries(ALIAS_MAP)) {
    // Keys are static constants — no ReDoS risk from regex
    result = result.replaceAll(new RegExp(String.raw`\b${from}\b`, 'g'), to);
  }
  return result;
};

/** Tokenize: normalize → apply aliases → split → filter short tokens → sort. */
const tokenize = (str: string): string[] =>
  applyAliases(normalize(str))
    .split(' ')
    .filter((t) => t.length > 1)
    .sort((a, b) => a.localeCompare(b));

/** Levenshtein edit distance between two strings (single-row DP). */
const levenshtein = (a: string, b: string): number => {
  const m = a.length;
  const n = b.length;
  const prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    let prevDiag = prev[0];
    prev[0] = i;
    for (let j = 1; j <= n; j++) {
      const temp = prev[j];
      prev[j] =
        a[i - 1] === b[j - 1]
          ? prevDiag
          : 1 + Math.min(prevDiag, prev[j - 1], prev[j]);
      prevDiag = temp;
    }
  }
  return prev[n];
};

/**
 * Token-set similarity between two token arrays (0–1).
 * Two tokens "match" if identical or Levenshtein distance ≤ 1
 * (handles transliterations like cruiser/cruizer automatically).
 */
const tokenSimilarity = (tokens1: string[], tokens2: string[]): number => {
  const total = Math.max(tokens1.length, tokens2.length);
  if (total === 0) return 0;

  let matched = 0;
  const used = new Set<number>();

  for (const t1 of tokens1) {
    for (let i = 0; i < tokens2.length; i++) {
      if (used.has(i)) continue;
      if (t1 === tokens2[i] || levenshtein(t1, tokens2[i]) <= 1) {
        matched++;
        used.add(i);
        break;
      }
    }
  }
  return matched / total;
};

const MATCH_THRESHOLD = 0.7;

/**
 * Find the best matching entry in ukraine-product-list-mapping.json for a given
 * product name + sfdcProductCode pair.
 *
 * Uses normalized token comparison with alias substitution and fuzzy matching
 * so that new treatments are matched automatically — only completely foreign
 * translations (e.g. NEPROTRUYENE → "non treated") need an alias entry.
 */
export const findMappingEntry = (
  productName: string,
  sfdcProductCode: string
): (typeof ukraineProductMapping)[number] | null => {
  if (!sfdcProductCode || !productName) return null;

  const normalizedPrefix = normalize(productName);
  const sfdcTokens = tokenize(sfdcProductCode);

  // Pre-filter: only entries whose hybrid_name starts with the product name
  const candidates = ukraineProductMapping.filter(
    (entry) =>
      entry.hybrid_name !== null &&
      normalize(entry.hybrid_name).startsWith(normalizedPrefix)
  );

  let bestEntry: (typeof ukraineProductMapping)[number] | null = null;
  let bestScore = 0;

  for (const candidate of candidates) {
    const candidateTokens = tokenize(candidate.hybrid_name);
    const score = tokenSimilarity(sfdcTokens, candidateTokens);
    if (score > bestScore) {
      bestScore = score;
      bestEntry = candidate;
    }
  }

  return bestScore >= MATCH_THRESHOLD ? bestEntry : null;
};

export interface UkraineHybridInput {
  name: string;
  sfdcProductCode: string;
  quantity: number;
  light: 1 | 2 | 3; // 1 = strongly recommended, 2 = second, 3 = third
}

export interface UkraineHybridPayloadItem {
  id: number;
  light: 1 | 2 | 3;
  qtt: number;
}

/**
 * Generates a Ukraine store seed-selector URL by converting sku.sfdcProductCode
 * values to the Ukrainian hybrid_name format, looking up their E-com product IDs
 * in ukraine-product-list-mapping.json, and encoding the payload as base64.
 *
 * Payload shape: [{ id, light, qtt }, ...]
 *   id    — E-com product ID from the mapping file
 *   light — recommendation rank: 1 (strongly recommended), 2, or 3
 *   qtt   — total bags count across all fields
 *
 * When no hybrids match the mapping, returns `fallbackUrl` (defaults to `''`).
 */
export const generateUkraineStoreUrl = (
  recommendedHybrids: UkraineHybridInput[],
  fallbackUrl = ''
): string => {
  const payload: UkraineHybridPayloadItem[] = recommendedHybrids
    .filter((hybrid) => hybrid.quantity > 0)
    .map((hybrid) => {
      const mapping = findMappingEntry(hybrid.name, hybrid.sfdcProductCode);
      if (!mapping) {
        return null;
      }
      return {
        id: mapping.id,
        light: hybrid.light,
        qtt: hybrid.quantity,
      };
    })
    .filter((item): item is UkraineHybridPayloadItem => item !== null);

  if (payload.length === 0) {
    return fallbackUrl;
  }

  const encoded = Base64.encode(JSON.stringify(payload));
  // Strip trailing '=' padding characters without regex to avoid ReDoS risk
  let end = encoded.length;
  while (end > 0 && encoded[end - 1] === '=') {
    end -= 1;
  }
  return `${UKRAINE_SEED_SELECTOR_BASE_URL}${encoded.slice(0, end)}`;
};
